import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserPlus } from 'lucide-react';
import { motion } from 'framer-motion';
import DashboardLayout from '@/components/mafia/DashboardLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DashboardNetworkPage() {
  const navigate = useNavigate();
  const { gamer, loading } = useMafiaAuth();
  const [tab, setTab] = useState('following');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);

  useEffect(() => {
    if (!loading && !gamer) navigate('/mafia/login', { replace: true });
  }, [loading, gamer, navigate]);

  useEffect(() => {
    if (!gamer) return;
    (async () => {
      try {
        const [f, g] = await Promise.all([
          fetch(`${API}/api/mafia/auth/profile/${gamer.member_no}/followers`).then(r => r.ok ? r.json() : { followers: [] }),
          fetch(`${API}/api/mafia/auth/profile/${gamer.member_no}/following`).then(r => r.ok ? r.json() : { following: [] }),
        ]);
        setFollowers(f.followers || []);
        setFollowing(g.following || []);
      } catch { /* ignore */ }
    })();
  }, [gamer]);

  if (loading || !gamer) {
    return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;
  }

  const TABS = [
    { k: 'following', l: 'Following', count: following.length, icon: UserPlus },
    { k: 'followers', l: 'Followers', count: followers.length, icon: Users },
  ];
  const list = tab === 'followers' ? followers : following;

  return (
    <DashboardLayout title="Network" subtitle="Your circle">
      {/* Animated tab bar */}
      <div className="relative flex items-center gap-1 ms-surface-1 ms-border p-1 mb-6 w-fit" style={{ borderRadius: 'var(--ms-radius-md)' }} data-testid="network-tabs">
        {TABS.map((opt) => (
          <button
            key={opt.k}
            onClick={() => setTab(opt.k)}
            data-testid={`network-tab-${opt.k}`}
            className={`relative px-4 py-1.5 flex items-center gap-2 text-xs font-medium transition-colors ${tab === opt.k ? 'ms-text' : 'ms-text-2 hover:ms-text'}`}
            style={{ borderRadius: 'var(--ms-radius-sm)' }}
          >
            {tab === opt.k && (
              <motion.span
                layoutId="network-pill"
                className="absolute inset-0"
                style={{ background: 'var(--ms-surface-3)', border: '1px solid var(--ms-border-subtle)', borderRadius: 'var(--ms-radius-sm)' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              />
            )}
            <opt.icon size={12} className="relative" />
            <span className="relative">{opt.l} <span className="ms-text-3">({opt.count})</span></span>
          </button>
        ))}
      </div>

      <section className="ms-card" data-testid="network-list">
        {list.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 mx-auto mb-4" style={{ background: 'var(--ms-surface-2)', borderRadius: '999px' }}>
              <Users size={20} className="ms-text-red" />
            </div>
            <p className="text-sm ms-text-2 max-w-sm mx-auto">
              {tab === 'following'
                ? "You're not following anyone yet. Visit other players' profiles to follow them."
                : 'No followers yet. Share your profile link to grow your network.'}
            </p>
          </div>
        ) : (
          <div className="ms-divide">
            {list.map((u) => (
              <button
                key={u.member_no}
                onClick={() => navigate(`/p/${u.member_no}`)}
                data-testid={`network-row-${u.member_no}`}
                className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-[var(--ms-surface-2)] transition-colors"
              >
                {(u.custom_avatar || u.picture) ? (
                  <img src={u.custom_avatar || u.picture} alt={u.name} className="w-10 h-10 object-cover" style={{ borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }} />
                ) : (
                  <div className="w-10 h-10 flex items-center justify-center ms-text-red font-semibold" style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }}>
                    {(u.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm ms-text font-medium truncate">{u.name}</p>
                  <p className="text-xs ms-text-3 mt-0.5">{u.member_no}</p>
                </div>
                <span className="ms-chip ms-chip-red">View →</span>
              </button>
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
